import React, { useState, useEffect } from 'react';
import ModalHeader from '../common/ModalHeader';
import FormField from '../common/FormField';
import CommentSection from '../common/CommentSection';
import ActionButtons from '../common/ActionButtons';
import { useToastService } from '../toast/ToastService';
import './AddWellNoteModal.scss';
import { CategoryData } from '../task-details-widget/TaskDataModels';
import { WellData, WellGroupData } from '../add-task-modal/AddTaskDataModals';
import { fetchWells, fetchWellGroups, fetchCategories } from '../../services/dashboard/TaskDetailsService';
import { addWellNote, EventData } from '../slices/WellNoteSlice';
import { WellNoteSaveData } from './WellEventData';
import { saveWellNoteData } from '../../services/dashboard/AddWellNoteService';
import { fetchPumpCheckerEvents } from '../../dispatchers/EventsDispatcher';
import { useAppDispatch, useAppSelector } from '../../hooks/storeHooks';
import { createPortal } from 'react-dom';

interface AddWellNoteModalProps {
  isOpen: boolean;
  onClose: () => void;
  onNoteSaved?: () => void;
  wellNoteToEdit?: EventData | null;
  currentWellId?: number | string | null;
  isPC?: boolean | undefined;
  assetId?: string;
  currentWellName?: string;
}

interface WellNoteFormData {
  well: string;
  wellGroup: string;
  category: string;
  dueDate: Date | null;
  wellNoteComment: string;
}

interface FormErrors {
  [key: string]: string | undefined;
}

interface TouchedFields {
  [key: string]: boolean;
}

const initialFormData: WellNoteFormData = {
  well: '',
  wellGroup: '',
  category: '',
  dueDate: null,
  wellNoteComment: '',
};

const AddWellNoteModal: React.FC<AddWellNoteModalProps> = ({
  isOpen,
  onClose,
  onNoteSaved,
  wellNoteToEdit,
  currentWellId,
  isPC,
  assetId,
  currentWellName,
}) => {
  const dispatch = useAppDispatch();
  const wellTestId = useAppSelector((state: any) => state.WellTestIdSlice?.data?.WellId);
  const endDate = useAppSelector((state: any) => state.AssetTrendDataSlice.endDate);
  const [formData, setFormData] = useState<WellNoteFormData>({ ...initialFormData });
  const [errors, setErrors] = useState<FormErrors>({});
  const [, setTouched] = useState<TouchedFields>({});
  const [wells, setWells] = useState<WellData[]>([]);
  const [wellGroups, setWellGroups] = useState<WellGroupData[]>([]);
  const [categories, setCategories] = useState<CategoryData[]>([]);
  const [saving, setSaving] = useState<boolean>(false);
  const { showSuccessToast, showErrorToast } = useToastService();

  // Debug props
  useEffect(() => {
    console.log('AddWellNoteModal props:', { isOpen, wellNoteToEdit, currentWellId });
  }, [isOpen, wellNoteToEdit, currentWellId]);

  useEffect(() => {
    const initializeFormData = () => {
      // Reset form data and errors when modal is closed
      if (!isOpen) {
        setFormData({ ...initialFormData });
        setErrors({});
        setTouched({});
        return;
      }

      // Set default values or edit values
      if (!wellNoteToEdit) {
        // Adding new well note - set defaults
        const defaultWellId = currentWellId || wellTestId;
        const newFormData = {
          ...initialFormData,
          well: defaultWellId?.toString() || '',
          dueDate: new Date(), // Current date for new notes
        };
        setFormData(newFormData);
      } else {
        // Editing existing well note - pre-fill form with current values
        const noteDate = wellNoteToEdit.Created
          ? new Date(wellNoteToEdit.Created)
          : wellNoteToEdit.timestamp
            ? new Date(wellNoteToEdit.timestamp)
            : new Date();

        const editFormData = {
          well: wellNoteToEdit.wellId?.toString() || currentWellId?.toString() || wellTestId?.toString() || '',
          wellGroup: wellNoteToEdit.wellGroupId?.toString() || '',
          category: wellNoteToEdit.categoryId?.toString() || '',
          dueDate: noteDate,
          wellNoteComment: wellNoteToEdit.Comment || wellNoteToEdit.description || '',
        };
        setFormData(editFormData);
      }
    };

    const initializeDropdowns = async () => {
      try {
        const [wellsData, wellGroupsData, categoriesData] = await Promise.all([
          fetchWells(isPC),
          fetchWellGroups(),
          fetchCategories(isPC),
        ]);
        setWells(wellsData);
        isPC ? setWellGroups(wellGroupsData) : setWellGroups([{ Name: 'All wells', WellGroupId: 0 }]); // Clear well groups if not PC
        setCategories(categoriesData);
        const categoryIdToSelect = categoriesData?.find((c: CategoryData) => {
          const categoryName = c.Name;
          return categoryName === wellNoteToEdit?.Category?.toString();
        });
        setFormData(prev => ({
          ...prev,
          category: categoryIdToSelect ? categoryIdToSelect.CategoryId?.toString() : prev.category
        }));
      } catch (error) {
        console.error('Error loading dropdown data:', error);
      }
    };

    if (isOpen) {
      initializeFormData();
      initializeDropdowns();
    } else {
      initializeFormData(); // Just reset form when closed
    }
  }, [isOpen, wellTestId, wellNoteToEdit, currentWellId]);

  const handleInputChange = (name: string, value: string | Date | null) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === 'well' ? { wellGroup: '' } : {}),
      ...(isPC && name === 'wellGroup' ? { well: '' } : {}),
    }));

    setTouched((prev) => ({ ...prev, [name]: true }));

    const error = validateField(name, value);
    setErrors((prev) => ({
      ...prev,
      [name]: error || undefined,
      ...(name === 'well' ? { wellGroup: undefined } : {}),
      ...(isPC && name === 'wellGroup' ? { well: undefined } : {}),
    }));
  };

  const validateField = (name: string, value: string | Date | null): string => {
    if (name === 'wellNoteComment' && (!value || (typeof value === 'string' && value.trim() === ''))) {
      return 'Please enter a note';
    }
    if (name === 'dueDate' && !value) return 'Please select a date';
    if (name === 'category' && (!value || value === '')) return 'Category is required';
    return '';
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    let valid = true;

    ['wellNoteComment', 'dueDate'].forEach((field) => {
      const error = validateField(field, formData[field as keyof WellNoteFormData]);
      if (error) {
        newErrors[field] = error;
        valid = false;
      }
    });

    const hasWell = !!formData.well;
    const hasWellGroup = !!formData.wellGroup;
    const hasCatefgory = !!formData.category;

    if (!hasCatefgory) {
      newErrors.category = 'Category is required';
      valid = false;
    }
    if (isPC) {
      if (!hasWell && !hasWellGroup) {
        newErrors.well = 'Please select either a Well or a Well Group';
        newErrors.wellGroup = 'Please select either a Well or a Well Group';
        valid = false;
      } else if (hasWell && hasWellGroup) {
        newErrors.well = 'Select only one: Well or Well Group';
        newErrors.wellGroup = 'Select only one: Well or Well Group';
        valid = false;
      }
    } else {
      if (!hasWell) {
        newErrors.well = 'Please select PC either a Well or a Well Group';
        valid = false;
      }

    }

    setErrors(newErrors);
    return valid;
  };

  const handleSave = async () => {
    setTouched(Object.fromEntries(Object.keys(formData).map((k) => [k, true])));

    const isValid = validateForm();
    if (!isValid) {
      return;
    }

    const rawDate = formData.dueDate || new Date();
    const normalizedDate = new Date(rawDate);
    normalizedDate.setUTCHours(5, 0, 0, 0);

    const payload: WellNoteSaveData = {
      Note: formData.wellNoteComment.trim(),
      NoteDate: rawDate.toISOString(),
      CategoryId: Number(formData.category),
      WellEventCreated: normalizedDate.toISOString(),
      WellId: formData.well,
      WellGroupId: formData.wellGroup ? Number(formData.wellGroup) : undefined,
      WellNoteId: wellNoteToEdit ? wellNoteToEdit.id : undefined,
    };

    if (isPC && !payload.WellId && !payload.WellGroupId) {
      return;
    }
    setSaving(true);
    try {
      const savedEvent = await saveWellNoteData(payload, isPC ?? false, assetId);
      if (savedEvent) {
        dispatch(addWellNote(savedEvent));

        if (endDate && wellTestId) {
          const newEndDate = new Date(endDate);
          newEndDate.setUTCHours(5, 0, 0, 0);
          const newNextDate = new Date(newEndDate);
          newNextDate.setUTCDate(newEndDate.getUTCDate() + 1);

          const formattedDate = newNextDate.toISOString();

          dispatch(
            fetchPumpCheckerEvents({
              wellId: wellTestId,
              startDate: newEndDate.toISOString(),
              endDate: formattedDate,
            }),
          );
        }
        showSuccessToast(
          wellNoteToEdit ? 'Well comment updated and chart refreshed' : 'Well comment saved and chart updated',
        );
        onClose();
        onNoteSaved?.();
      } else {
        showErrorToast('Failed to save well comment.');
      }
    } catch (err: unknown) {
      showErrorToast('Error saving well note.');
    } finally {
      setSaving(false);
    }
  };
  if (!isOpen) return null;
  return createPortal(
    <div className='note-well-modal'>
      <div className='modal-overlay'>
        <div className='modal-container'>
          <div className='modal-main-container'>
            <ModalHeader title={`${wellNoteToEdit ? 'Edit comment' : 'Add new comment'} ${currentWellName ? `- ${currentWellName}` : ''}`} onClose={onClose} />
            <main className='form-main'>
              <section className='form-section'>
                <div className='form-row'>
                  <FormField
                    name='well'
                    label='Well name'
                    value={formData.well}
                    onChange={handleInputChange}
                    type='select'
                    required
                    error={errors.well}
                    options={wells.map((w) => ({ key: w.Key.toString(), value: w.Value }))}
                  />
                  <FormField
                    name='wellGroup'
                    label='Well group'
                    value={formData.wellGroup}
                    onChange={handleInputChange}
                    type='select'
                    required
                    error={errors.wellGroup}
                    options={wellGroups.map((g) => ({ key: g.WellGroupId?.toString(), value: g.Name }))}
                  />
                </div>
                <CommentSection
                  label='Note'
                  value={formData.wellNoteComment}
                  onChange={(value) => handleInputChange('wellNoteComment', value)}
                  placeholder='Enter well note'
                  required
                  error={errors.wellNoteComment}
                />
                <div className='form-row' style={{ marginTop: '20px' }}>
                  <FormField
                    name='category'
                    label='Category'
                    value={formData.category}
                    onChange={handleInputChange}
                    required
                    error={errors.category}
                    type='select'
                    options={categories.map((c) => ({
                      key: c.CategoryId?.toString(),
                      value: c.Name,
                    }))}
                  />
                  <FormField
                    name='dueDate'
                    label='Note date'
                    value={formData.dueDate}
                    onChange={handleInputChange}
                    type='date'
                    required
                    error={errors.dueDate}
                  />

                </div>
              </section>
            </main>
            <footer className='form-footer'>
              <ActionButtons buttonText='Save' onSubmit={handleSave} onCancel={onClose} isSubmitting={saving} />
            </footer>
          </div>
        </div>
      </div>
    </div>,
    document.querySelector('.outlet-container') || document.body,
  );
};

export default AddWellNoteModal;
